import { EVENT_NAME } from '@leagueplatform/analytics';
import { ParagraphText, UtilityText } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { ANALYTICS_SCREEN_NAMES, ANALYTICS_DETAILS } from 'common/constants';
import { useOnboardingError } from 'components/onboarding-account-setup/contexts/onboarding-error.context';
import { STEP_STRING_IDS } from 'components/onboarding-account-setup/onboarding-account-setup.constants';
import { StepScreen } from 'components/step-screen/step-screen.component';
import { useStepNavigation } from 'components/stepper/stepper.component';
import { useSetMarketingPreferences } from 'hooks/mutations/use-set-marketing-preferences/use-set-marketing-preferences.hook';
import { useOnboardingAnalytics } from 'hooks/use-onboarding-analytics/use-onboarding-analytics.hook';
import { ReactNode, useEffect } from 'react';

const renderParagraph = (chunk: ReactNode) => (
  <ParagraphText
    css={{
      color: '$onSurfaceTextSubdued',
      '&:not(:last-of-type)': { marginBlockEnd: '$half' },
    }}
  >
    {chunk}
  </ParagraphText>
);

export const MarketingStep = () => {
  const { formatMessage } = useIntl();
  const { goToNextStep } = useStepNavigation();

  const setMarketingPreferencesMutation = useSetMarketingPreferences();
  const { reportError } = useOnboardingError();

  const trackEvent = useOnboardingAnalytics(
    ANALYTICS_SCREEN_NAMES.ACCOUNT_SETUP_MARKETING_CONSENT,
  );

  useEffect(() => {
    trackEvent(EVENT_NAME.SCREEN_LOADED);
  }, [trackEvent]);

  const handleMarketingOnClick = async (isMarketingAccepted: boolean) => {
    trackEvent(EVENT_NAME.BUTTON_CLICKED, {
      detail: isMarketingAccepted
        ? ANALYTICS_DETAILS.CONFIRM_MARKETING
        : ANALYTICS_DETAILS.DECLINE_MARKETING,
    });

    try {
      const result = await setMarketingPreferencesMutation.mutateAsync({
        email: isMarketingAccepted,
      });

      // The data processor can return a transport-level success while still
      // reporting that the write failed via `result.success`. Only advance
      // once the preference is actually persisted, otherwise the step would
      // reappear on reload because the profile flag is still unset.
      if (result.success) {
        goToNextStep();
        return;
      }

      reportError(
        new Error(result.message ?? 'Failed to set marketing preferences'),
      );
    } catch (err) {
      reportError(err as Error);
    }
  };

  const { isPending, variables } = setMarketingPreferencesMutation;

  return (
    <StepScreen
      heading={formatMessage({ id: STEP_STRING_IDS.marketing.heading })}
      primaryButton={{
        label: formatMessage({
          id: STEP_STRING_IDS.marketing.primaryButtonLabel,
        }),
        onClick: () => handleMarketingOnClick(true),
        isButtonLoading: isPending && variables?.email === true,
      }}
      secondaryButton={{
        label: formatMessage({
          id: STEP_STRING_IDS.marketing.secondaryButtonLabel,
        }),
        onClick: () => handleMarketingOnClick(false),
        isButtonLoading: isPending && variables?.email === false,
      }}
    >
      <UtilityText emphasis="subtle">
        {formatMessage(
          { id: STEP_STRING_IDS.marketing.body },
          { p: renderParagraph },
        )}
      </UtilityText>
    </StepScreen>
  );
};
